import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Mail, ShieldCheck } from "lucide-react";
import SiteLogo from "../../components/SiteLogo";
import PageSeo from "../../components/PageSeo";
import TurnstileField from "../../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../../turnstileConfig";
import { isApiConfigured, postForgotPassword } from "../../apiClient";
import old1_PublicSiteFooter from "../components/old1_PublicSiteFooter";

function old1_ForgotPasswordPage() {
  const navigate = useNavigate();
  const signinHref = "/old1/signin";
  const signupHref = "/old1/signup";
  const [email, setEmail] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    const normalized = String(email || "").trim().toLowerCase();
    if (!normalized) {
      setSubmitError("Enter your email address.");
      return;
    }

    if (!isApiConfigured()) {
      setSubmitError("The app is not connected to the API. Password reset is unavailable in this mode.");
      return;
    }

    setSubmitting(true);
    try {
      if (isTurnstileEnabled() && !turnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && turnstileToken ? String(turnstileToken) : undefined;
      await postForgotPassword({ email: normalized, turnstileToken: turnstilePayload });
      setDone(true);
      setTurnstileKey((k) => k + 1);
      setTurnstileToken(null);
    } catch (err) {
      let raw = err instanceof Error ? err.message : "Something went wrong";
      if (!raw || raw === "Something went wrong") {
        raw = "Something went wrong. Please try again.";
      }
      if (raw === "Failed to fetch" || /networkerror|load failed|fetch/i.test(raw)) {
        raw = "Could not reach the server. Check your connection and try again.";
      }
      setSubmitError(formatTurnstileErrorForUser(raw));
      setTurnstileKey((k) => k + 1);
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  const showTurnstile = isTurnstileEnabled() && isApiConfigured();

  const authCard = (
    <section className="signup-card jw-public-card jw-auth-card">
      <Link className="back-link jw-public-back-link" to={signinHref}>
        <ArrowLeft className="jw-public-icon" aria-hidden="true" />
        Back to sign in
      </Link>
      <p className="eyebrow jw-public-eyebrow">Forgot password</p>
      <h2>Reset your password</h2>
      {done ? (
        <>
          <p className="subtitle">
            If an account exists for that email, we sent instructions to reset your password. Check
            your inbox and spam folder.
          </p>
          <p className="verification-help" style={{ marginTop: "1rem" }}>
            <Link className="change-email-link" to={signinHref}>
              Return to sign in
            </Link>
          </p>
        </>
      ) : (
        <>
          <p className="subtitle">
            Enter the email you use for JOBUWIN. We&apos;ll send you a link to choose a new password.
          </p>
          <form
            id="jobuwin-forgot-password-form"
            className="signup-form jw-auth-form"
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <label htmlFor="forgotEmail">Email address</label>
            <div className="jw-auth-field">
              <Mail className="jw-public-icon" aria-hidden="true" />
              <input
                id="forgotEmail"
                name="forgotEmail"
                type="email"
                autoComplete="username"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {!showTurnstile && submitError ? (
              <p className="form-inline-alert-danger" role="alert">
                {submitError}
              </p>
            ) : null}

            {!showTurnstile ? (
              <button
                type="submit"
                className="submit-button jw-public-button jw-public-button--primary"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send reset link"}
                <ArrowRight className="jw-public-icon" aria-hidden="true" />
              </button>
            ) : null}
          </form>

          {showTurnstile ? (
            <div className="signup-form-captcha-sibling">
              {submitError === TURNSTILE_REQUIRED_MESSAGE ? (
                <p className="turnstile-inline-alert" role="alert">
                  {submitError}
                </p>
              ) : null}
              <TurnstileField
                tokenSessionId={0}
                onTokenChange={(token) => {
                  setTurnstileToken(token);
                  if (token) {
                    setSubmitError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
                  }
                }}
                widgetKey={`forgot-password-${turnstileKey}`}
                options={{ action: "forgot-password" }}
              />
              <p className="turnstile-human-note">
                Security check: making sure you are a genuine human :-)
              </p>
              {submitError && submitError !== TURNSTILE_REQUIRED_MESSAGE ? (
                <p className="form-inline-alert-danger" role="alert">
                  {submitError}
                </p>
              ) : null}
              <button
                type="submit"
                form="jobuwin-forgot-password-form"
                className="submit-button jw-public-button jw-public-button--primary"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send reset link"}
                <ArrowRight className="jw-public-icon" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );

  return (
    <div className="signup-page jw-public-page jw-auth-page">
      <PageSeo
        title="Forgot password"
        description="Reset your JOBUWIN password securely and get back to genuine hiring conversations."
        path="/old1/forgot-password"
        noindex
      />
      <nav className="landing-nav jw-public-nav" aria-label="Password reset navigation">
        <div className="landing-nav-shell jw-public-nav__shell">
          <SiteLogo />
          <div className="nav-buttons landing-nav-actions">
            <button
              type="button"
              className="jw-public-button jw-public-button--text landing-nav-login"
              onClick={() => navigate(signinHref)}
            >
              Sign in
            </button>
            <button
              type="button"
              className="jw-public-button jw-public-button--primary landing-nav-cta"
              onClick={() => navigate(signupHref)}
            >
              Sign up
              <ArrowRight className="jw-public-icon" aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>

      <main id="main-content" className="signup-container jw-auth-shell">
        <aside className="jw-auth-aside" aria-label="Password reset support">
          <span className="jw-public-chip">
            <ShieldCheck className="jw-public-icon" aria-hidden="true" />
            Account recovery
          </span>
          <h1>Reset securely and get back to JOBUWIN.</h1>
          <p>We send password reset instructions only to the email linked to your account.</p>
          <div className="jw-auth-benefits">
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Secure reset link by email</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Security check protects the request</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Existing sign-in flow stays unchanged</span>
          </div>
        </aside>
        {authCard}
      </main>

      <old1_PublicSiteFooter />
    </div>
  );
}

export default old1_ForgotPasswordPage;
